import { qoursesApi } from '@/api/qourses.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { Input } from '@/shadcn/components/ui/input.tsx'
import { Label } from '@/shadcn/components/ui/label.tsx'
import { ToastVariant, minDelay, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Globe, Loader2, Mail } from 'lucide-react'
import { useState } from 'react'

export default function CreateLeadModal() {
  const [isLoading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const queryClient = useQueryClient()

  // List of common generic email providers (German-focused)
  const genericEmailProviders = [
    // International providers
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'icloud.com',
    'protonmail.com',
    'yandex.com',
    'mail.com',
    'zoho.com',
    'fastmail.com',
    'live.com',
    'msn.com',
    'me.com',
    'mac.com',
    // German providers
    'gmx.de',
    'gmx.net',
    'web.de',
    't-online.de',
    'freenet.de',
    '1und1.de',
    '1&1.de',
    'arcor.de',
    'alice.de',
    'vodafone.de',
    'telekom.de',
    'bluewin.ch',
    'sunrise.ch',
    'swissonline.ch',
    // European providers
    'yahoo.de',
    'hotmail.de',
    'outlook.de',
    'posteo.de',
    'mailbox.org',
    'tutanota.com',
    'tutanota.de',
    // Austrian providers
    'gmx.at',
    'yahoo.at',
    'hotmail.at',
    'outlook.at',
    'chello.at',
    'aon.at',
    // Swiss providers (additional)
    'hispeed.ch',
    'vtx.ch',
    'green.ch',
  ]

  // Auto-populate website when email loses focus and domain is not generic
  const handleEmailBlur = () => {
    if (email && email.includes('@') && !websiteUrl) {
      const emailParts = email.split('@')
      if (emailParts.length === 2) {
        const domain = emailParts[1].toLowerCase().trim()

        // Only auto-populate if it's not a generic provider
        if (domain && !genericEmailProviders.includes(domain)) {
          setWebsiteUrl(`https://${domain}`)
        }
      }
    }
  }

  const handleCreateLead = async () => {
    if (!email.trim()) {
      sendNotification('Validation Error', 'Email address is required', ToastVariant.Error)
      return
    }

    try {
      setLoading(true)
      await minDelay(
        qoursesApi.salesAdmin.salesAdminControllerCreateLeadV1({
          email: email.trim(),
          websiteUrl: websiteUrl.trim() || undefined,
        }),
        1000,
      )

      // Invalidate leads query to refresh the list
      queryClient.invalidateQueries(['admin', 'leads'])

      sendNotification(
        'Lead Created',
        'The lead has been created successfully',
        ToastVariant.Success,
      )
      popAllModals()
    } catch (e) {
      if (e instanceof Error) {
        sendNotification('Creation Failed', e.message, ToastVariant.Error, 4000)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dynamic.Content className="p-6 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogTitle className="mt-6 sm:mt-2">Create New Lead</DialogTitle>
        <DialogDescription>Add a new lead to the system</DialogDescription>
      </DialogHeader>
      <div className="mb-8 space-y-6">
        {/* Email Field */}
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email Address *
          </Label>
          <Input
            type="email"
            id="email"
            placeholder="lead@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={handleEmailBlur}
            disabled={isLoading}
            required
          />
        </div>

        {/* Website URL Field */}
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="websiteUrl" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Website URL
          </Label>
          <Input
            type="url"
            id="websiteUrl"
            placeholder="https://example.com"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            disabled={isLoading}
          />
          {websiteUrl && (
            <a
              href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" />
              Open website
            </a>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={popAllModals} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleCreateLead} disabled={isLoading} variant={'indigo'}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Lead'
          )}
        </Button>
      </div>
    </Dynamic.Content>
  )
}
